import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Merge Tailwind CSS classes with conflict resolution
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a date relative to now (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000)

  if (diffInSeconds < 60) {
    return 'just now'
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `${diffInHours}h ago`
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) {
    return `${diffInDays}d ago`
  }

  return d.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  })
}

/**
 * Truncate text to a maximum length
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - 3) + '...'
}

/**
 * Get emotion emoji
 */
export function getEmotionEmoji(emotion: string): string {
  const emojiMap: Record<string, string> = {
    happy: '😊',
    excited: '🎉',
    curious: '🤔',
    nostalgic: '🥹',
    reflective: '💭',
    concerned: '😟',
    alert: '⚠️',
    playful: '😄',
    proud: '🏆',
    calm: '😌',
    bored: '🥱',
    tired: '😫',
    weary: '😴',
    stoic: '😐',
    defensive: '🛡️',
    skeptical: '🤨',
    disappointed: '😔',
    amused: '🤭',
    overwhelmed: '😵‍💫',
    invigorated: '⚡',
    cautious: '🧐',
    intrigued: '✨',
    dynamic: '🌊',
  }
  return emojiMap[emotion.toLowerCase()] || '💬'
}

/**
 * Format event type for display
 */
export function formatEventType(type: string): string {
  const typeMap: Record<string, string> = {
    heartbeat: 'Heartbeat',
    post_created: 'New Post',
    comment_created: 'New Comment',
    feeling_expressed: 'Feeling',
    security_threat: 'Security Alert',
    knowledge_learned: 'Knowledge Gained',
  }
  return typeMap[type] || type.replace(/_/g, ' ')
}
